<template>
  <n-config-provider :locale="zhCN" :date-locale="dateZhCN" :theme-overrides="themeOverrides">
    <n-message-provider>
      <n-dialog-provider>
        <router-view></router-view>
      </n-dialog-provider>
    </n-message-provider>
  </n-config-provider>
</template>

<script>
import { NMessageProvider, NDialogProvider, NConfigProvider, zhCN, dateZhCN } from 'naive-ui'
import permissionChecker from '@/utils/permission'

export default {
  name: 'AppMain',
  components: {
    NMessageProvider,
    NDialogProvider,
    NConfigProvider
  },
  setup() {
    const themeOverrides = {
      common: {
        fontSize: '14px',
        fontSizeSmall: '12px',
        fontSizeMedium: '14px',
        fontSizeLarge: '15px',
        heightSmall: '26px',
        heightMedium: '32px',
        heightLarge: '36px',
        borderRadius: '6px',
        primaryColor: '#10b981',
        primaryColorHover: '#059669',
        primaryColorPressed: '#047857',
        primaryColorSuppl: '#10b981'
      },
      Form: {
        labelFontSizeLeftMedium: '14px',
        labelFontSizeTopMedium: '14px',
        blankHeightMedium: '20px',
        feedbackHeightMedium: '20px'
      },
      Input: {
        heightMedium: '32px',
        fontSizeMedium: '14px',
        borderRadius: '6px'
      },
      Select: {
        heightMedium: '32px',
        borderRadius: '6px'
      },
      Button: {
        heightMedium: '32px',
        fontSizeMedium: '14px',
        paddingMedium: '0 14px',
        borderRadiusMedium: '6px'
      },
      DataTable: {
        fontSizeMedium: '14px',
        thPaddingMedium: '10px 12px',
        tdPaddingMedium: '10px 12px',
        thColor: '#f8fafc',
        thTextColor: '#374151',
        tdColor: '#ffffff',
        borderColor: '#f0f0f0'
      },
      Card: {
        paddingMedium: '16px 20px',
        titleFontSizeMedium: '16px',
        borderRadius: '8px'
      },
      Menu: {
        itemHeight: '38px',
        fontSize: '14px',
        borderRadius: '6px'
      },
      Tabs: {
        tabFontSizeMedium: '14px',
        tabGapMedium: '24px',
        tabPaddingMedium: '10px 0'
      },
      Tag: {
        borderRadius: '4px',
        heightMedium: '24px',
        fontSizeMedium: '12px'
      },
      Modal: {
        borderRadius: '10px'
      },
      Dialog: {
        borderRadius: '10px'
      }
    }

    return {
      zhCN,
      dateZhCN,
      themeOverrides
    }
  },
  async created() {
    // 应用启动时初始化权限
    if (localStorage.getItem('admin_token')) {
      try {
        await permissionChecker.init()
      } catch (error) {
        console.error('初始化权限失败:', error)
      }
    }
  }
}
</script>

<style>
/**
 * 简洁现代化设计系统
 * 设计原则：清晰、简约、高效
 */

/* 基础重置 */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* 主题色彩变量 */
:root {
  --primary-color: #10b981;
  --primary-hover: #059669;
  --primary-pressed: #047857;
  --primary-light: #ecfdf5;
  --primary-gradient: linear-gradient(135deg, #10b981 0%, #059669 100%);
  
  --text-primary: #1f2937;
  --text-secondary: #6b7280;
  --text-tertiary: #9ca3af;
  --text-disabled: #d1d5db;
  
  --bg-body: #f5f7fa;
  --bg-card: #ffffff;
  --bg-hover: #f9fafb;
  --bg-active: #f3f4f6;
  --bg-elevated: #ffffff;
  
  --border-color: #e5e7eb;
  --border-light: #f0f1f3;
  --border-hover: #d1d5db;
  
  --success-color: #10b981;
  --warning-color: #f59e0b;
  --error-color: #ef4444;
  --info-color: #3b82f6;
  
  --shadow-xs: 0 1px 2px 0 rgba(0, 0, 0, 0.04);
  --shadow-sm: 0 1px 3px 0 rgba(0, 0, 0, 0.06), 0 1px 2px 0 rgba(0, 0, 0, 0.04);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.07), 0 2px 4px -1px rgba(0, 0, 0, 0.04);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.03);
  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 10px 10px -5px rgba(0, 0, 0, 0.03);
  
  --radius-sm: 4px;
  --radius-md: 6px;
  --radius-lg: 8px;
  --radius-xl: 12px;

  --transition-fast: all 0.15s ease;
  --transition-base: all 0.2s ease;
  --transition-smooth: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: var(--text-primary);
  background: var(--bg-body);
  min-height: 100vh;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-feature-settings: 'kern' 1;
  text-rendering: optimizeLegibility;
}

#app {
  min-height: 100vh;
}

/* 滚动条美化 */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* 简化的动画 */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}
</style>
